import { IMAGE_UPLOAD_SIZE } from '@/constants';
import * as z from 'zod';

// Define your schema here
export const doctorSchema = z.object({
  prefix: z.string().min(1, 'Must select one option from the dropdown'),
  firstName: z
    .string()
    .regex(/^[A-Za-z]+$/, 'Must contain only alphabets. No spaces.')
    .min(1, 'Enter a name')
    .max(50, 'Maximum length: 50 characters'),
  lastName: z
    .string()
    .regex(/^[A-Za-z]+$/, 'Must contain only alphabets. No spaces.')
    .min(1, 'Enter a name')
    .max(50, 'Maximum length: 50 characters'),
  phoneNumber: z
    .string()
    .regex(
      /^\d{10,}$/,
      'Must be a valid phone number. Minimum length: 10 digits'
    ),
  email: z
    .string()
    .email('Enter valid email address containing "@" ')
    .max(50, 'Maximum length: 50 characters'),
  country: z.string().min(1, 'Must select one country from the dropdown'),
  city: z.string().min(1, 'Must select one option from the dropdown'),
  state: z.string().optional(),
  address1: z.string().min(1).max(100, 'Maximum length: 100 characters'),
  address2: z.string().max(100, 'Maximum length: 100 characters').optional(),
  zipCode: z
    .string()
    .regex(/^\d{4,10}$/, 'Must contain only numbers. Length: 4-10 digits'),
  languages: z.array(
    z.string().min(1, 'At least one language must be selected')
  ),
  aboutMe: z
    .string()
    .max(500, 'About me should not exceed 500 characters')
    .optional(),
  signature:
    z
      .object({
        fileType: z
          .enum(['image/png', 'image/jpeg'])
          .or(z.literal('image/jpg')),
        fileSize: z
          .number()
          .max(IMAGE_UPLOAD_SIZE, 'File size must not exceed 5 MB'),
      })
      .optional() || '',
  stamp:
    z
      .object({
        fileType: z
          .enum(['image/png', 'image/jpeg'])
          .or(z.literal('image/jpg')),
        fileSize: z
          .number()
          .max(IMAGE_UPLOAD_SIZE, 'File size must not exceed 5 MB'),
      })
      .optional() || '',
  // gender: z.string().optional(),
  speciality: z.string().min(1, 'Must select one option from the dropdown'),
  specialization: z.string().min(1, 'Must select one option from the dropdown'),
  qualification: z.string().min(1, 'Must select one option from the dropdown'),
  registrationNumber: z
    .string()
    .regex(/^[A-Za-z0-9]+$/, 'Must be alphanumeric')
    .min(5, 'Minimum length: 5 characters')
    .max(30, 'Maximum length: 30 characters'),
  registrationValidity: z
    .date({
      message: 'Registration validity date is required',
    })
    .refine((date) => date > new Date(), {
      message: 'Enter correct validity',
    }),
  // birthDate: z.date().optional(),
  msaSigningDate: z.date({
    message: 'MSA signing date is required',
  }),
  practitionerStartDate: z.date({
    message: 'Practitioner start date is required',
  }),
  practitionerEndDate: z.date().optional(),
  pricing: z
    .string()
    .regex(/^\d{1,4}$/, 'Pricing must be a number with up to 4 digits')
    .refine((value) => parseInt(value, 10) >= 1, 'At least add ₹1'),
  indemnityValidity: z.date({
    message: 'Indemnity validity date is required',
  }),
  educationRows: z.array(
    z.object({
      years: z
        .number()
        .refine(
          (year) => new Date(year).getFullYear() <= new Date().getFullYear(),
          {
            message: 'Should not be in the future',
          }
        ),
      body: z
        .string()
        .min(1, 'Must add your education')
        .max(100, 'Maximum length: 100 characters'),
      title: z.string().min(1, 'Must select one option from the dropdown'),
      displayTop: z.union([z.boolean(), z.null()]).optional(),
      displayBottom: z.union([z.boolean(), z.null()]).optional(),
    })
  ),
});

export const DEFAULT_ADD_DOCTOR_SCHEMA_VALUES = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  city: '',
  country: '',
  state: '',
  address1: '',
  address2: '',
  zipCode: '',
  languages: [],
  aboutMe: '',
  prefix: '',
  speciality: '',
  specialization: '',
  qualification: '',
  signature: undefined,
  stamp: undefined,
  registrationNumber: '',
  // gender: '',
  // birthDate: undefined,
  registrationValidity: undefined,
  msaSigningDate: undefined,
  practitionerStartDate: undefined,
  practitionerEndDate: undefined,
  pricing: '',
  indemnityValidity: undefined,
  educationRows: [
    {
      years: undefined,
      body: '',
      title: '',
      displayTop: undefined,
      displayBottom: undefined,
    },
  ],
};

export type DoctorFormInputs = z.infer<typeof doctorSchema>;

export interface Concept {
  code: string;
  display: string;
}

export interface Include {
  concept?: Concept[];
}

export interface Compose {
  include?: Include[];
}

export interface ValueSetData {
  compose?: Compose;
}

// Define the types for your options
export interface Option {
  value: string;
  label: string;
}
